import React, { useMemo } from 'react';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import { useParams } from 'react-router-dom';
import GridSection from 'components/_layouts/GridSection';
import PhxBadge from '_lex/_atoms/PhxBadge';
import useUrlParams from 'hooks/useUrlParams';
import datetimeHelper from 'helpers/datetimeHelper';
import { useEisFlaggedPersonnel } from '../../_data_sources/useEisFlaggedPersonnel';
import type { EisFlaggedPersonnelItem, EisFlagStatus } from '../../_types';

export type FlaggedPersonnelParams = {
  search?: string | null;
  status?: EisFlagStatus | null;
  groups?: string | null;
};

const defaultParams: FlaggedPersonnelParams = {
  search: null,
  status: null,
  groups: null,
};

function getStatusBadgeColor(status: EisFlagStatus): 'success' | 'warning' | 'default' {
  if (status === 'Active') return 'success';
  if (status === 'Cleared') return 'warning';
  return 'default';
}

function getStatusLabelKey(status: EisFlagStatus): string {
  const key = status.toLowerCase();
  return `performanceReporting.eis.details.flaggedPersonnel.status.${key}`;
}

export default function FlaggedPersonnel() {
  const { id } = useParams<{ id: string }>();
  const ruleId = id ? Number(id) : 0;
  const [params, setParams] = useUrlParams(defaultParams);

  const flaggedPersonnelQuery = useEisFlaggedPersonnel(ruleId, {}, {}, (err) => {
    console.error('Error fetching EIS flagged personnel:', err);
  });

  const allItems = flaggedPersonnelQuery.data?.pages.flatMap((p) => p.content) ?? [];
  const totalCount = flaggedPersonnelQuery.data?.pages[0]?.count ?? 0;

  const columns = useMemo(
    () => ({
      name: {
        header: t('performanceReporting.eis.details.flaggedPersonnel.columns.name'),
        flex: 2,
        sortable: true,
        valueGetter: (_: unknown, row: EisFlaggedPersonnelItem) => row?.name ?? '',
        renderer: (params: { row: EisFlaggedPersonnelItem }) => (
          <Typography variant='body1'>{params.row?.name ?? '--'}</Typography>
        ),
        onClick: () => {},
      },
      flaggedDate: {
        header: t('performanceReporting.eis.details.flaggedPersonnel.columns.flaggedDate'),
        flex: 1.5,
        sortable: true,
        valueGetter: (_: unknown, row: EisFlaggedPersonnelItem) => row?.flaggedDate ?? '',
        renderer: (params: { row: EisFlaggedPersonnelItem }) => (
          <Typography variant='body1'>
            {datetimeHelper.formatDate(params.row?.flaggedDate, { missingDisplay: '--' })}
          </Typography>
        ),
      },
      groups: {
        header: t('performanceReporting.eis.details.flaggedPersonnel.columns.groups'),
        flex: 1.5,
        valueGetter: (_: unknown, row: EisFlaggedPersonnelItem) => row?.groups ?? '--',
        renderer: (params: { row: EisFlaggedPersonnelItem }) => (
          <Typography variant='body1'>{params.row?.groups ?? '--'}</Typography>
        ),
      },
      totalRiskScore: {
        header: t('performanceReporting.eis.details.flaggedPersonnel.columns.totalRiskScore'),
        flex: 1.5,
        valueGetter: (_: unknown, row: EisFlaggedPersonnelItem) => row?.totalRiskScore ?? 0,
        renderer: (params: { row: EisFlaggedPersonnelItem }) => {
          const row = params.row;
          const score = row?.totalRiskScore ?? 0;
          const over = row?.overThresholdBy ?? 0;
          return (
            <Stack
              direction='row'
              spacing={1}
              alignItems='center'
            >
              <Typography variant='body1'>{score}</Typography>
              {over > 0 && (
                <PhxBadge
                  color='default'
                  size='sm'
                >
                  {t('performanceReporting.eis.details.flaggedPersonnel.overThreshold', '', { count: String(over) })}
                </PhxBadge>
              )}
            </Stack>
          );
        },
      },
      status: {
        header: t('performanceReporting.eis.details.flaggedPersonnel.columns.status'),
        flex: 1,
        valueGetter: (_: unknown, row: EisFlaggedPersonnelItem) => row?.status ?? '',
        renderer: (params: { row: EisFlaggedPersonnelItem }) => {
          const status = params.row?.status;
          if (!status) return <Typography variant='body1'>--</Typography>;
          const color = getStatusBadgeColor(status);
          const label = t(getStatusLabelKey(status));
          return (
            <PhxBadge
              color={color}
              size='sm'
            >
              {label}
            </PhxBadge>
          );
        },
      },
    }),
    [t],
  );

  const isLoading = flaggedPersonnelQuery.isLoading;
  const isError = flaggedPersonnelQuery.isError;

  return (
    <Stack
      spacing={4}
      sx={{ minHeight: 0 }}
    >
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Box sx={{ p: 4, textAlign: 'center', color: 'error.main' }}>
          {t('performanceReporting.eis.details.flaggedPersonnel.errorLoading')}
        </Box>
      ) : allItems.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            minHeight: 200,
            p: 4,
          }}
        >
          <Typography
            variant='body1'
            sx={{ fontWeight: 600 }}
          >
            {t('performanceReporting.eis.details.flaggedPersonnel.emptyState.title')}
          </Typography>
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ mt: 1 }}
          >
            {t('performanceReporting.eis.details.flaggedPersonnel.emptyState.subtitle')}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ minHeight: 400 }}>
          <GridSection
            columns={columns}
            data={allItems}
            query={flaggedPersonnelQuery}
            totalCount={totalCount}
            gridKey={`flagged-personnel-${ruleId}-${params.search ?? ''}-${params.status ?? ''}-${params.groups ?? ''}`}
            gridProps={{ getRowId: (row: EisFlaggedPersonnelItem) => row.id }}
          />
        </Box>
      )}
    </Stack>
  );
}
